import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Image as ImageIcon, ChevronLeft, Video, User, Film, Wand2, Lock, Info } from 'lucide-react';
import CachedImg from '../components/CachedImg';
import { supabase } from '../lib/supabase';

type ProductImage = { id: string; url: string; principal: boolean; orden: number; variante_id: string };
type Variant = { id: string; nombre: string; images?: ProductImage[] };

type TipoVideoKey = 'influencer' | 'cinematografico' | 'personalizado';

// Options that are coming soon and cannot be selected
const COMING_SOON_OPTIONS: TipoVideoKey[] = ['cinematografico', 'personalizado'];

export const DisenarVideo: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const idsParam = params.get('ids') || '';
  const ids = useMemo(() => idsParam.split(',').map(s => s.trim()).filter(Boolean), [idsParam]);

  const [variants, setVariants] = useState<Variant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [signedUrls, setSignedUrls] = useState<Record<string, { full: string; thumbnail: string }>>({});
  const [selectedTipoVideo, setSelectedTipoVideo] = useState<TipoVideoKey>('influencer');
  const [modoPro, setModoPro] = useState<boolean>(false);
  const [userPlan, setUserPlan] = useState<string | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<boolean>(true);
  const [showProPlanModal, setShowProPlanModal] = useState<boolean>(false);
  const isProPlanAllowed = userPlan === 'negocio' || userPlan === 'enterprise';

  // Fetch variants
  useEffect(() => {
    let cancelled = false;
    const fetchVariants = async () => {
      if (ids.length === 0) {
        setLoading(false);
        return;
      }
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('No user');

        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();

        if (!profile?.id_company) throw new Error('No company');
        const idCompany = profile.id_company as string;
        if (!cancelled) setCompanyId(idCompany);

        const { data: variantsData, error: variantsError } = await supabase
          .from('variantes')
          .select('id, nombre')
          .in('id', ids)
          .eq('id_company', idCompany);

        if (variantsError) throw variantsError;
        if (!variantsData || variantsData.length === 0) {
          if (!cancelled) setVariants([]);
          return;
        }

        const variantIds = variantsData.map(v => v.id);
        const { data: imagesData } = await supabase
          .from('productos_imagenes')
          .select('id, url, principal, orden, variante_id')
          .in('variante_id', variantIds)
          .eq('id_company', idCompany);

        const imagesByVariant: Record<string, ProductImage[]> = {};
        (imagesData || []).forEach((img: any) => {
          if (!imagesByVariant[img.variante_id]) imagesByVariant[img.variante_id] = [];
          imagesByVariant[img.variante_id].push(img as ProductImage);
        });

        const merged: Variant[] = variantsData.map(v => ({
          ...v,
          images: imagesByVariant[v.id] || [],
        }));

        if (!cancelled) setVariants(merged);
      } catch (e) {
        console.error('Error fetching variants:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchVariants();
    return () => { cancelled = true; };
  }, [ids]);

  // Fetch user plan for Modo Pro check
  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setLoadingPlan(false);
          return;
        }
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (!profile?.id_company) {
          setLoadingPlan(false);
          return;
        }
        const { data: company } = await supabase
          .from('company_profiles')
          .select('plan')
          .eq('id_company', profile.id_company)
          .single();
        if (company?.plan) {
          setUserPlan(company.plan);
          // If user doesn't have pro plan access and modoPro is enabled, disable it
          if (company.plan !== 'negocio' && company.plan !== 'enterprise') {
            setModoPro(false);
          }
        }
      } catch (err) {
        console.error('Error fetching user plan:', err);
      } finally {
        setLoadingPlan(false);
      }
    };
    fetchUserPlan();
  }, []);

  // Sign URLs for images
  useEffect(() => {
    if (!companyId || variants.length === 0) return;
    let cancelled = false;

    const signUrls = async () => {
      const newSignedUrls: Record<string, { full: string; thumbnail: string }> = {};

      for (const v of variants) {
        const principalImg = v.images?.find(i => i.principal) || v.images?.[0];
        if (!principalImg) continue;

        const fullPath = `${companyId}/${v.id}/full/${principalImg.url}`;
        const optPath = `${companyId}/${v.id}/opt/${principalImg.url}`;

        try {
          const { data: fullData } = await supabase.storage.from('productos').createSignedUrl(fullPath, 3600);
          const { data: optData } = await supabase.storage.from('productos').createSignedUrl(optPath, 3600);

          if (!cancelled) {
            newSignedUrls[v.id] = {
              full: fullData?.signedUrl || '',
              thumbnail: optData?.signedUrl || fullData?.signedUrl || '',
            };
          }
        } catch (e) {
          console.error('Error signing URL:', e);
        }
      }

      if (!cancelled) setSignedUrls(prev => ({ ...prev, ...newSignedUrls }));
    };

    signUrls();
    return () => { cancelled = true; };
  }, [companyId, variants]);

  const principalImageUrl = (v: Variant): string => {
    return signedUrls[v.id]?.thumbnail || '';
  };

  const tipoVideoOptions: Array<{ id: TipoVideoKey; name: string; description: string; icon: React.ReactNode; color: string }> = [
    {
      id: 'influencer',
      name: 'Influencer',
      description: 'Video estilo contenido de redes sociales, dinámico y cercano',
      icon: <User className="w-5 h-5" />,
      color: 'pink',
    },
    {
      id: 'cinematografico',
      name: 'Cinematográfico',
      description: 'Video con estética profesional y tomas cinematográficas',
      icon: <Film className="w-5 h-5" />,
      color: 'purple',
    },
    {
      id: 'personalizado',
      name: 'Personalizado',
      description: 'Describí exactamente cómo querés que sea tu video',
      icon: <Wand2 className="w-5 h-5" />,
      color: 'emerald',
    },
  ];

  const getColorClasses = (color: string, isSelected: boolean) => {
    const colors: Record<string, { border: string; bg: string; iconBg: string; iconText: string; check: string }> = {
      pink: {
        border: isSelected ? 'border-pink-500' : 'border-gray-200',
        bg: isSelected ? 'bg-pink-50' : 'bg-white hover:bg-gray-50',
        iconBg: 'bg-pink-50',
        iconText: 'text-pink-600',
        check: 'bg-pink-500',
      },
      purple: {
        border: isSelected ? 'border-purple-500' : 'border-gray-200',
        bg: isSelected ? 'bg-purple-50' : 'bg-white hover:bg-gray-50',
        iconBg: 'bg-purple-50',
        iconText: 'text-purple-600',
        check: 'bg-purple-500',
      },
      emerald: {
        border: isSelected ? 'border-emerald-500' : 'border-gray-200',
        bg: isSelected ? 'bg-emerald-50' : 'bg-white hover:bg-gray-50',
        iconBg: 'bg-emerald-50',
        iconText: 'text-emerald-600',
        check: 'bg-emerald-500',
      },
    };
    return colors[color] || colors.pink;
  };

  return (
    <div className="p-4 sm:p-8 sm:pb-0 max-w-5xl mx-auto min-h-[100dvh] md:min-h-screen bg-white sm:bg-transparent">
      <div className="sm:bg-white sm:rounded-2xl sm:shadow-sm p-0 sm:p-6 mt-14 sm:mt-0">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="sm:hidden inline-flex items-center justify-center w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-700"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900">Diseñá tu video</h1>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              if (selectedTipoVideo === 'influencer') {
                const url = `/redes-sociales/crear-anuncio/configurar-video-influencer?ids=${ids.join(',')}${modoPro ? '&modo_pro=1' : ''}`;
                navigate(url);
              }
              // Cinematográfico y Personalizado por ahora no hacen nada
            }}
            className="hidden sm:inline-flex items-center justify-center gap-2 px-5 h-10 rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm bg-blue-500 hover:bg-blue-600 text-white"
          >
            Siguiente
          </button>
        </div>

        {/* Productos seleccionados */}
        <div className="mt-6">
          <h2 className="text-sm font-medium text-gray-500 mb-2 ml-2">Productos seleccionados</h2>
          <div className="flex items-center gap-3 overflow-x-auto pb-1">
            {loading && (
              <div className="text-sm text-gray-600">Cargando...</div>
            )}
            {!loading && variants.length === 0 && (
              <div className="text-sm text-gray-600">No hay productos seleccionados</div>
            )}
            {!loading && variants.map(v => {
              const url = principalImageUrl(v);
              return (
                <div key={v.id} className="shrink-0 w-20 h-20 rounded-xl border border-gray-200 overflow-hidden bg-gray-100">
                  {url ? (
                    <img src={url} alt={v.nombre} className="w-full h-full object-cover" loading="lazy" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                      <ImageIcon className="w-6 h-6" />
                    </div>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => {
                const qp = new URLSearchParams();
                if (ids.length > 0) qp.set('ids', ids.join(','));
                const query = qp.toString();
                navigate(query ? `/redes-sociales/crear-anuncio/seleccionar-productos?${query}` : '/redes-sociales/crear-anuncio/seleccionar-productos');
              }}
              className="self-center inline-flex items-center gap-2 px-4 h-10 rounded-full border border-gray-200 bg-white text-gray-900 shadow-sm hover:bg-gray-50 text-sm shrink-0"
            >
              Cambiar
            </button>
          </div>
        </div>

        {/* Tipo de Video */}
        <div className="mt-8">
          <h2 className="text-sm font-medium text-gray-500 mb-3 ml-2">Tipo de video</h2>
          <div className="flex flex-col gap-3">
            {tipoVideoOptions.map(option => {
              const isComingSoon = COMING_SOON_OPTIONS.includes(option.id);
              const isSelected = selectedTipoVideo === option.id;
              const colorClasses = getColorClasses(option.color, isSelected && !isComingSoon);

              return (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => !isComingSoon && setSelectedTipoVideo(option.id)}
                  disabled={isComingSoon}
                  className={`relative flex items-start gap-4 rounded-2xl border text-left p-4 transition-colors ${isComingSoon ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-70' : `${colorClasses.border} ${colorClasses.bg}`}`}
                >
                  <div className={`shrink-0 rounded-xl p-2.5 ${isComingSoon ? 'bg-gray-100' : colorClasses.iconBg}`}>
                    <span className={isComingSoon ? 'text-gray-400' : colorClasses.iconText}>{option.icon}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className={`font-medium text-sm ${isComingSoon ? 'text-gray-500' : 'text-gray-900'}`}>{option.name}</p>
                      {isComingSoon && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-600">
                          Próximamente
                        </span>
                      )}
                    </div>
                    <p className={`mt-1 text-xs ${isComingSoon ? 'text-gray-400' : 'text-gray-600'}`}>{option.description}</p>
                  </div>
                  {isSelected && !isComingSoon && (
                    <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${colorClasses.check} text-white text-xs`}>
                      ✓
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Calidad de Video - Modo Pro */}
        <div className="mt-8">
          <h2 className="text-sm font-medium text-gray-500 mb-3 ml-2">Calidad de video</h2>
          <div className="border border-gray-200 rounded-2xl p-3 pb-4 sm:p-4 sm:pb-5 bg-white relative">
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">Modo Pro</h3>
                  {!loadingPlan && !isProPlanAllowed && (
                    <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      <Lock className="w-3 h-3" />
                      Plan Pro / Ultra
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  Generá videos con mayor calidad y definición.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={modoPro}
                onClick={() => {
                  if (isProPlanAllowed) {
                    setModoPro(!modoPro);
                  } else if (!loadingPlan) {
                    setShowProPlanModal(true);
                  }
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 cursor-pointer ${
                  modoPro && isProPlanAllowed ? 'bg-blue-500' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    modoPro && isProPlanAllowed ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
            {isProPlanAllowed && (
              <p className="text-xs text-gray-900 mt-3 font-medium flex items-center gap-1.5">
                <Info className="w-4 h-4 text-blue-500 flex-shrink-0" />
                Un video en Modo Pro consume 72 imágenes.
              </p>
            )}

            {/* Pro Plan Modal */}
            {showProPlanModal && (
              <>
                <div 
                  className="fixed inset-0 z-40"
                  onClick={() => setShowProPlanModal(false)}
                />
                <div className="absolute right-0 top-full mt-2 z-50 w-72 bg-white rounded-xl shadow-lg border border-gray-200 p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <Lock className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-semibold text-gray-900">Función exclusiva</h4>
                      <p className="text-xs text-gray-600 mt-1">
                        El Modo Pro está disponible solo para los planes Pro y Ultra.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setShowProPlanModal(false);
                      navigate('/subscription');
                    }}
                    className="mt-4 w-full bg-blue-500 hover:bg-blue-600 text-white text-sm font-normal py-2 px-4 rounded-full shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                  >
                    Ver planes
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Spacer for mobile bottom bar */}
        <div className="sm:hidden h-24" />
      </div>

      {/* Mobile bottom bar */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 z-30">
        <button
          type="button"
          onClick={() => {
            if (selectedTipoVideo === 'influencer') {
              const url = `/redes-sociales/crear-anuncio/configurar-video-influencer?ids=${ids.join(',')}${modoPro ? '&modo_pro=1' : ''}`;
              navigate(url);
            }
            // Cinematográfico y Personalizado por ahora no hacen nada
          }}
          className="w-full inline-flex items-center justify-center gap-2 h-10 bg-blue-500 hover:bg-blue-600 text-white rounded-full shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-sm"
        >
          Siguiente
        </button>
      </div>
    </div>
  );
};

export default DisenarVideo;
